import React, { useEffect, useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { useAuthStore, isAdmin } from '../store/authStore';
import TilePool from './TilePool';
import DropZone from './DropZone';
import TypingInput from './TypingInput';
import GameControls from './GameControls';
import SettingsModal from './SettingsModal';
import StrokeModal from './StrokeModal';
import LessonEditor from './LessonEditor';

const Game = () => {
  const { 
    lessons, 
    currentLessonIndex, 
    setCurrentLesson,
    lessonLoading,
    lessonLoadError,
    openSettings,
    showTilePinyin,
    setShowTilePinyin,
    showTileEnglish,
    setShowTileEnglish,
    randomMode,
    setRandomMode,
  } = useGameStore();
  const { user } = useAuthStore();
  const [editorOpen, setEditorOpen] = useState(false);
  
  const currentLesson = lessons[currentLessonIndex];
  
  // Lazy-load full lesson on first visit, and initialize slot contents
  useEffect(() => {
    if (!currentLesson) return;
    
    // If we only have metadata (no dialogue), trigger the full load
    if (!currentLesson.dialogue || currentLesson.dialogue.length === 0) {
      setCurrentLesson(currentLessonIndex);
      return;
    }
    
    const { slotContents } = useGameStore.getState();
    if (slotContents.length === 0 && currentLesson.dialogue[0]?.breakdown) {
      useGameStore.setState({
        slotContents: new Array(currentLesson.dialogue[0].breakdown.length).fill(null)
      });
    }
  }, [currentLessonIndex, lessons, currentLesson, setCurrentLesson]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const { prevSentence, nextSentence, checkAnswer, clearSelection } = useGameStore.getState();
      
      if (e.key === 'ArrowLeft') prevSentence();
      if (e.key === 'ArrowRight') nextSentence();
      if (e.key === 'Enter') checkAnswer();
      if (e.key === 'Escape') clearSelection();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  if (!currentLesson) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
      Loading...
    </div>;
  }
  
  // Lesson chosen but full content still loading
  if (lessonLoading || !currentLesson.dialogue || currentLesson.dialogue.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
        {lessonLoadError ? (
          <>
            <p style={{ color: '#ef4444' }}>⚠️ {lessonLoadError}</p>
            <button
              onClick={() => setCurrentLesson(currentLessonIndex)}
              style={{
                marginTop: '12px',
                padding: '8px 18px',
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </>
        ) : (
          <>Loading lesson {currentLesson.code}...</>
        )}
      </div>
    );
  }
  
  const headerStyle = {
    textAlign: 'center',
    marginBottom: '24px',
  };
  
  const headerRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '8px',
  };
  
  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#f1f5f9',
    margin: 0,
  };
  
  const settingsButtonStyle = {
    background: 'none',
    border: '1px solid #334155',
    color: '#94a3b8',
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    fontSize: '1.1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s, color 0.15s',
  };
  
  const subtitleStyle = {
    fontSize: '0.85rem',
    color: '#94a3b8',
  };
  
  const lessonBarStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '24px',
  };
  
  const selectStyle = {
    background: '#1e293b',
    border: '1px solid #334155',
    color: '#e2e8f0',
    padding: '10px 14px',
    borderRadius: '10px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    maxWidth: '100%',
    minWidth: '280px',
  };
  
  const audioStyle = {
    height: '36px',
    maxWidth: '280px',
  };
  
  const sectionStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    maxWidth: '720px',
    margin: '0 auto',
    padding: '20px',
  };
  
  const handleLessonChange = (e) => {
    setCurrentLesson(parseInt(e.target.value));
  };
  
  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', padding: '20px' }}>
      <div style={sectionStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={headerRowStyle}>
            <h1 style={titleStyle}>🧩 Sentence Builder</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.8rem',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                title="Toggle pinyin on tiles"
              >
                <span>Pinyin</span>
                <span
                  className={`toggle-switch ${showTilePinyin ? 'on' : ''}`}
                  onClick={() => setShowTilePinyin(!showTilePinyin)}
                >
                  <span className="toggle-knob" />
                </span>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.8rem',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                title="Toggle English on tiles"
              >
                <span>English</span>
                <span
                  className={`toggle-switch ${showTileEnglish ? 'on' : ''}`}
                  onClick={() => setShowTileEnglish(!showTileEnglish)}
                >
                  <span className="toggle-knob" />
                </span>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.8rem',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                title="Pick sentences randomly within this lesson"
              >
                <span>Random</span>
                <span
                  className={`toggle-switch ${randomMode ? 'on' : ''}`}
                  onClick={() => setRandomMode(!randomMode)}
                >
                  <span className="toggle-knob" />
                </span>
              </label>
            </div>
            <button 
              style={settingsButtonStyle}
              onClick={openSettings}
              title="Settings"
              onMouseEnter={(e) => {
                e.target.style.background = '#1e293b';
                e.target.style.color = '#e2e8f0';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'none';
                e.target.style.color = '#94a3b8';
              }}
            >
              ⚙
            </button>
          </div>
          <div style={subtitleStyle}>
            {currentLesson.code} — {currentLesson.title}
          </div>
        </div>
        
        {/* Lesson Selector & Audio */}
        <div style={lessonBarStyle}>
          <select 
            style={selectStyle}
            value={currentLessonIndex}
            onChange={handleLessonChange}
          >
            {lessons.map((lesson, index) => (
              <option key={lesson.code} value={index}>
                {lesson.code} — {lesson.title}
              </option>
            ))}
          </select>
          
          {currentLesson.audio && (
            <audio 
              controls 
              preload="none"
              style={audioStyle}
            >
              <source src={currentLesson.audio} type="audio/mpeg" />
            </audio>
          )}
          
          {/* Edit button: visible to admins (any lesson) or owners (own lessons).
              Hidden for ad-hoc practice lessons (e.g. MY-SENTENCES) because those
              characters don't have database ids. */}
          {user && currentLesson.id && (isAdmin(user) || currentLesson.createdById === user.id) && (
            <button
              onClick={() => setEditorOpen(true)}
              style={{
                padding: '10px 14px',
                background: '#1e293b',
                border: '1px solid #6366f1',
                color: '#a5b4fc',
                borderRadius: '10px',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
              title="Edit pinyin, radicals, and meanings for this sentence"
            >
              ✏️ Edit
            </button>
          )}
        </div>
        
        {/* Game Controls (Sentence Navigation) */}
        <GameControls />
        
        {/* Tile Pool */}
        <TilePool />
        
        {/* Drop Zone */}
        <DropZone />
        
        {/* Typing Input - alternative to tiles */}
        <TypingInput />
        
      </div>
      
      {/* Modals */}
      <SettingsModal />
      <StrokeModal />
      <LessonEditor open={editorOpen} onClose={() => setEditorOpen(false)} />
      
      {/* Global Styles */}
      <style>{`
        @keyframes pulse-green {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
          50% { box-shadow: 0 0 0 12px rgba(34, 197, 94, 0); }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        
        .tile:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        
        .tile:active {
          cursor: grabbing;
        }
        
        .section-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #64748b;
          margin-bottom: 8px;
        }
        
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #6366f1;
          cursor: pointer;
          border: 2px solid #818cf8;
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #6366f1;
          cursor: pointer;
          border: 2px solid #818cf8;
        }

        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 38px;
          height: 22px;
          background: #334155;
          border-radius: 11px;
          transition: background 0.2s;
          cursor: pointer;
          flex-shrink: 0;
        }
        .toggle-switch.on {
          background: #6366f1;
        }
        .toggle-knob {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 18px;
          height: 18px;
          background: #f1f5f9;
          border-radius: 50%;
          transition: transform 0.2s, background 0.2s;
        }
        .toggle-switch.on .toggle-knob {
          transform: translateX(16px);
        }
      `}</style>
    </div>
  );
};

export default Game;
